import { faInfoCircle, faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError } from 'axios';
import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { Color } from '@/constants/color';
import emptySelectOption from '@/constants/emptySelectOption';
import FieldError from '@/forms/FieldError';
import useInsuranceVOB from '@/hooks/useInsuranceVOB';
import { ExternalVerificationSource } from '@/types/externalVerification';
import { Banner } from '@/ui';
import AutocompleteSingleSelect from '@/ui/components/AutocompleteSingleSelect';
import Button from '@/ui/components/Button';
import CopyContent from '@/ui/components/CopyContent';
import DataList, { DataRow } from '@/ui/components/DataList';
import Select from '@/ui/components/Form/Select';
import Heading from '@/ui/components/Heading';
import Spinner from '@/ui/components/Spinner';
import Tooltip from '@/ui/components/Tooltip';

import { serviceCodeDropdownOptions } from '../serviceCodeOptions';
import { RunVOBFormValues, runVobSchema, verificationSourceOptions } from '../vobSchema';

import type { Props } from './types';

const RunVOBForm = ({
  insurancePolicy,
  firstName,
  lastName,
  birthday,
  dismissPolicyVerification,
  setExternalVerification,
  setResponseError
}: Props) => {
  const {
    carrier,
    subscriber_id,
    id: policyId,
    policyholder_first_name,
    policyholder_last_name,
    policyholder_dob
  } = insurancePolicy;

  const {
    formState: { errors, isValid },
    control,
    handleSubmit,
    trigger
  } = useForm({
    values: {
      first_name: firstName,
      last_name: lastName,
      birthday,
      policyholder_first_name,
      policyholder_last_name,
      policyholder_dob,
      carrier,
      subscriber_id,
      verification_source: ExternalVerificationSource.FRONTRUNNER,
      service_code: insurancePolicy.last_verification?.service_code ?? 'AI'
    },
    resolver: yupResolver(runVobSchema),
    mode: 'onChange'
  });

  const { mutateAsync: runInsuranceVOB, isPending: isRunningInsuranceVob } = useInsuranceVOB();

  useEffect(() => {
    // Display validation errors on load
    trigger();
  }, [trigger]);

  const runVob = async (values: RunVOBFormValues) => {
    if (!policyId || !values.service_code) return;
    setExternalVerification(null);
    setResponseError(null);
    try {
      const externalVerification = await runInsuranceVOB({
        policyId,
        data: {
          service_code: values.service_code,
          vendor: values.verification_source
        }
      });
      setExternalVerification(externalVerification);
      console.log('External verification', externalVerification);
    } catch (error) {
      if (error instanceof AxiosError && error.response?.data?.detail) {
        setResponseError(error.response?.data?.detail);
      } else {
        setResponseError('An error occurred while verifying the policy');
      }
    }
  };

  const hasPolicyholderInfo = insurancePolicy.has_policyholder_attrs;

  return (
    <form className="flex flex-col gap-2 my-2" onSubmit={handleSubmit(runVob)}>
      {!hasPolicyholderInfo && (
        <Banner
          label="Policyholder information is incomplete. Member's information will be used instead."
          color="yellow"
          icon={faInfoCircle}
        />
      )}
      <DataList className="[&>div]:flex [&>div]:flex-row [&>div]:gap-2">
        {hasPolicyholderInfo ? (
          <>
            <Tooltip tooltipContent="An insurance policyholder is the person or entity that owns an insurance policy and is responsible for paying premiums and managing the policy">
              <Heading className="mt-2" value={4} text="Policyholder">
                <FontAwesomeIcon icon={faQuestionCircle} className="ml-2" />
              </Heading>
            </Tooltip>
            <hr />
            <FieldError message={errors.policyholder_first_name?.message?.toString()} />
            <Controller
              name="policyholder_first_name"
              control={control}
              render={({ field: { value } }) => (
                <DataRow label="First Name:">
                  {value ? <CopyContent content={value} /> : '-'}
                </DataRow>
              )}
            />

            <FieldError message={errors.policyholder_last_name?.message?.toString()} />
            <Controller
              name="policyholder_last_name"
              control={control}
              render={({ field: { value } }) => (
                <DataRow label="Last Name:">
                  {value ? <CopyContent content={value} /> : '-'}
                </DataRow>
              )}
            />

            <FieldError message={errors.policyholder_dob?.message?.toString()} />
            <Controller
              name="policyholder_dob"
              control={control}
              render={({ field: { value } }) => <DataRow label="DOB:">{value ?? '-'}</DataRow>}
            />
            <hr />
          </>
        ) : (
          <>
            <Heading className="mt-2" value={4} text="Patient" />
            <hr />
            <FieldError message={errors.first_name?.message?.toString()} />
            <Controller
              name="first_name"
              control={control}
              render={({ field: { value } }) => (
                <DataRow label="First Name:">
                  {value ? <CopyContent content={value} /> : '-'}
                </DataRow>
              )}
            />

            <FieldError message={errors.last_name?.message?.toString()} />
            <Controller
              name="last_name"
              control={control}
              render={({ field: { value } }) => (
                <DataRow label="Last Name:">
                  {value ? <CopyContent content={value} /> : '-'}
                </DataRow>
              )}
            />

            <FieldError message={errors.birthday?.message?.toString()} />
            <Controller
              name="birthday"
              control={control}
              render={({ field: { value } }) => <DataRow label="DOB:">{value}</DataRow>}
            />
            <hr />
          </>
        )}

        <FieldError message={errors.carrier?.message?.toString()} />
        <Controller
          name="carrier"
          control={control}
          render={({ field: { value } }) => (
            <DataRow
              label="Carrier:"
              tooltipContent="Carrier (member selection) is what the PM selects in the app signup flow">
              {value}
            </DataRow>
          )}
        />

        <FieldError message={errors.subscriber_id?.message?.toString()} />
        <Controller
          name="subscriber_id"
          control={control}
          render={({ field: { value } }) => (
            <DataRow
              label="Subscriber ID:"
              tooltipContent={`The # also referred to as "Identification Number", "Member ID", "Patient ID", "Certificate #"`}>
              {value ? <CopyContent content={value} /> : '-'}
            </DataRow>
          )}
        />
      </DataList>
      <hr />
      <Controller
        name="verification_source"
        control={control}
        render={({ field: { onChange, value } }) => (
          <Select
            label="Verification Source"
            onChange={(option) => onChange(option.value)}
            options={verificationSourceOptions}
            placeholder="Select a Verification Source"
            selectedOption={
              verificationSourceOptions.find((option) => option.value === value) ??
              emptySelectOption
            }
          />
        )}
      />
      <Controller
        name="service_code"
        control={control}
        render={({ field: { onChange, value } }) => (
          <AutocompleteSingleSelect
            clearable
            label="Service Code"
            onChange={(option) => onChange(option?.value)}
            options={serviceCodeDropdownOptions}
            placeholder="Select Service Code"
            selectedOption={
              serviceCodeDropdownOptions.find((option) => option.value === value) ??
              emptySelectOption
            }
          />
        )}
      />

      {isRunningInsuranceVob ? (
        <div className="flex flex-col gap-2 my-2">
          <Spinner label="Running VOB..." />
          <Heading
            value={4}
            text="Sit tight, this may take a few seconds"
            className="text-gray-600"
          />
        </div>
      ) : (
        <div className="flex gap-4 mt-4">
          <Button
            ariaLabel="Run VOB"
            color={Color.WHITE}
            size="sm"
            className="flex-1"
            type="submit"
            disabled={!isValid}>
            Run VOB
          </Button>
          <Button
            ariaLabel="Cancel"
            onClick={dismissPolicyVerification}
            color={Color.RED}
            size="sm"
            className="flex-2">
            Cancel
          </Button>
        </div>
      )}
    </form>
  );
};
export default React.memo(RunVOBForm);
